import {
	isPaletteCustomOverridesActive,
	type PaletteConfig,
} from '../../../models/chart-config.model';
import { resolveEffectiveChartPaletteId } from '../../../utils/chart-palette-resolve.util';

type LineChartPaletteConfig = Pick<
	PaletteConfig,
	| 'selectedPaletteId'
	| 'inheritsReportThemePalette'
	| 'useDefaultPalette'
	| 'customLight'
	| 'customDark'
>;

type ThemeModeLike = 'light' | 'dark' | 'auto' | string;

type LineChartVisualizationConfig = {
	colors?: unknown;
};

type LineChartConfigLike = {
	palette?: LineChartPaletteConfig;
	visualization?: LineChartVisualizationConfig;
};

export type LineChartColorPolicy = {
	useVisualizationColors: boolean;
	clearSeriesColors: boolean;
};

type PaletteRecordLike = {
	light?: string[];
	dark?: string[];
};

export function resolveLineChartPaletteColors(options: {
	configPalette?: LineChartPaletteConfig;
	paletteId?: string;
	themeMode?: ThemeModeLike;
	reportThemePaletteId?: string;
	getPaletteById: (paletteId: string) => PaletteRecordLike | null;
	getColorsForPalette: (paletteId: string) => string[];
}): string[] {
	const {
		configPalette,
		paletteId,
		themeMode,
		reportThemePaletteId,
		getPaletteById,
		getColorsForPalette,
	} = options;
	const resolvedThemeMode =
		themeMode === 'dark' ? 'dark' : themeMode === 'light' ? 'light' : null;
	const selectedPaletteId = resolveEffectiveChartPaletteId({
		configPalette,
		cardPaletteInput: paletteId,
		reportThemePaletteId,
	});

	if (!selectedPaletteId) {
		return [];
	}

	if (configPalette && isPaletteCustomOverridesActive(configPalette)) {
		if (
			resolvedThemeMode === 'dark' &&
			Array.isArray(configPalette.customDark) &&
			configPalette.customDark.length > 0
		) {
			return configPalette.customDark;
		}
		if (
			resolvedThemeMode === 'light' &&
			Array.isArray(configPalette.customLight) &&
			configPalette.customLight.length > 0
		) {
			return configPalette.customLight;
		}
	}

	const palette = getPaletteById(selectedPaletteId);
	if (palette && resolvedThemeMode) {
		const modeColors =
			resolvedThemeMode === 'dark' ? palette.dark : palette.light;
		if (Array.isArray(modeColors) && modeColors.length > 0) {
			return modeColors;
		}
	}

	return getColorsForPalette(selectedPaletteId);
}

export function applyLineChartColorPolicy(
	options: {
		colors?: unknown;
		series?: unknown[];
		plotOptions?: { series?: { opacity?: unknown; states?: unknown } };
	},
	policy: LineChartColorPolicy,
	fallbackColors: string[]
): {
	colors?: unknown;
	series?: unknown[];
	plotOptions?: { series?: { opacity?: unknown; states?: unknown } };
} {
	const next = { ...options };

	if (!policy.useVisualizationColors) {
		next.colors = fallbackColors.length > 0 ? fallbackColors : undefined;
	}

	if (policy.clearSeriesColors) {
		const plotSeries = next.plotOptions?.series;
		if (
			plotSeries &&
			typeof plotSeries.opacity === 'number' &&
			plotSeries.opacity < 1
		) {
			next.plotOptions = {
				...(next.plotOptions || {}),
				series: {
					...plotSeries,
					opacity: 1,
				},
			};
		}
		if (Array.isArray(next.series)) {
			next.series = next.series.map(s => {
				const out = {
					...(s as {
						color?: string;
						opacity?: unknown;
						states?: { inactive?: { opacity?: unknown } };
					}),
				};
				if (typeof out.opacity === 'number' && out.opacity < 1) {
					out.opacity = 1;
				}
				if (
					out.states?.inactive &&
					typeof out.states.inactive.opacity === 'number' &&
					out.states.inactive.opacity < 1
				) {
					out.states = {
						...out.states,
						inactive: {
							...out.states.inactive,
							opacity: 1,
						},
					};
				}
				delete out.color;
				return out;
			});
		}
	}

	return next;
}

function hasSavedPalette(palette?: LineChartPaletteConfig): boolean {
	if (!palette) return false;
	if (
		typeof palette.selectedPaletteId === 'string' &&
		palette.selectedPaletteId.length > 0
	) {
		return true;
	}
	if (Array.isArray(palette.customLight) && palette.customLight.length > 0) {
		return true;
	}
	if (Array.isArray(palette.customDark) && palette.customDark.length > 0) {
		return true;
	}
	return false;
}

export function getLineChartColorPolicy(
	config?: LineChartConfigLike
): LineChartColorPolicy {
	const paletteIsSaved = hasSavedPalette(config?.palette);
	const visualizationColors = config?.visualization?.colors;
	const hasVisualizationColors =
		Array.isArray(visualizationColors) && visualizationColors.length > 0;
	const useVisualizationColors = paletteIsSaved && hasVisualizationColors;

	return {
		useVisualizationColors,
		clearSeriesColors: !useVisualizationColors,
	};
}
